import React, { useState, useEffect } from 'react';
import { X, Megaphone, Calendar, Clock, Users, MessageSquare } from 'lucide-react';

const CampaignModal = ({ isOpen, onClose, onSave, campaign }) => {
  const [formData, setFormData] = useState({
    nom: '',
    message: '',
    type_envoi: 'immédiat',
    date_planifiee: '',
    statut: 'brouillon'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (campaign) {
      setFormData({
        nom: campaign.nom || '',
        message: campaign.message || '',
        type_envoi: campaign.type_envoi || 'immédiat',
        date_planifiee: campaign.date_planifiee ? campaign.date_planifiee.split('T')[0] : '',
        statut: campaign.statut || 'brouillon'
      });
    } else {
      setFormData({
        nom: '',
        message: '',
        type_envoi: 'immédiat',
        date_planifiee: '',
        statut: 'brouillon'
      });
    }
    setErrors({});
  }, [campaign, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom de la campagne est obligatoire';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est obligatoire';
    } else if (formData.message.length > 160) {
      newErrors.message = 'Le message ne peut pas dépasser 160 caractères';
    }

    if (formData.type_envoi === 'planifié' && !formData.date_planifiee) {
      newErrors.date_planifiee = 'La date est obligatoire pour un envoi planifié';
    }

    if (formData.date_planifiee) {
      const selectedDate = new Date(formData.date_planifiee);
      const now = new Date();
      if (selectedDate <= now) {
        newErrors.date_planifiee = 'La date doit être dans le futur';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const campaignData = {
        ...formData,
        date_planifiee: formData.type_envoi === 'planifié' && formData.date_planifiee 
          ? new Date(formData.date_planifiee).toISOString() 
          : null
      };
      onSave(campaignData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      type_envoi: type,
      date_planifiee: type === 'immédiat' ? '' : prev.date_planifiee
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-secondary-900">
                  {campaign ? 'Modifier la campagne' : 'Nouvelle campagne'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Nom de la campagne */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Nom de la campagne *
                  </label>
                  <div className="mt-1 relative">
                    <Megaphone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                      type="text"
                      className={`input pl-10 ${errors.nom ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      value={formData.nom}
                      onChange={(e) => handleInputChange('nom', e.target.value)}
                      placeholder="Ex: Promotion Été 2024"
                    />
                  </div>
                  {errors.nom && (
                    <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Message SMS *
                  </label>
                  <div className="mt-1 relative">
                    <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-secondary-400" />
                    <textarea
                      className={`input pl-10 ${errors.message ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tapez votre message SMS ici..."
                      rows={4}
                      maxLength={160}
                    />
                  </div>
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    {formData.message.length}/160 caractères
                  </p>
                </div>

                {/* Type d'envoi */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-3">
                    Type d'envoi
                  </label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => handleTypeChange('immédiat')}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        formData.type_envoi === 'immédiat'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-secondary-300 hover:border-secondary-400'
                      }`}
                    >
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 mr-3" />
                        <div>
                          <div className="font-medium">Envoi immédiat</div>
                          <div className="text-sm text-secondary-500">
                            Envoyer maintenant
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTypeChange('planifié')}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        formData.type_envoi === 'planifié'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-secondary-300 hover:border-secondary-400'
                      }`}
                    >
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 mr-3" />
                        <div>
                          <div className="font-medium">Envoi planifié</div>
                          <div className="text-sm text-secondary-500">
                            Programmer l'envoi
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Date planifiée */}
                {formData.type_envoi === 'planifié' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Date et heure d'envoi *
                    </label>
                    <div className="mt-1 relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input
                        type="datetime-local"
                        className={`input pl-10 ${errors.date_planifiee ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                        value={formData.date_planifiee}
                        onChange={(e) => handleInputChange('date_planifiee', e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                    {errors.date_planifiee && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_planifiee}</p>
                    )}
                  </div>
                )}

                {/* Aperçu du message */}
                {formData.message && (
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-secondary-700 mb-2">
                      Aperçu du message
                    </h4>
                    <div className="bg-white border border-secondary-200 rounded p-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <MessageSquare className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-secondary-900">
                            MonEntreprise
                          </div>
                          <div className="mt-1 text-sm text-secondary-700">
                            {formData.message}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-secondary-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="submit"
                className="btn btn-primary w-full sm:w-auto sm:ml-3"
              >
                {campaign ? 'Modifier' : 'Créer la campagne'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;
